import React, {Fragment, useEffect, useState} from "react";
import {Exist} from "components/Exist";
import {HOCModal} from "components/HOCModal";
import {HOCModalCenterDesktopPosition} from "components/HOCModalCenterDesktopPosition";
import {useDispatch, useSelector} from "react-redux";
import {
	getRewardStatsByName,
	getUserAvatar,
	getUserRewards,
	isRewardViewed,
	loadingUserState,
} from "modules/selectors";
import {useTranslation} from "react-i18next";
import {useIsMobile} from "modules/utils";
import styled from "@emotion/styled";
import {ButtonWithIcon} from "components/ButtonWithIcon";
import {fetchUserRewards, setViewRewardModal} from "modules/actions/user";
import {RequestState} from "modules";
import {Box, LinearProgress} from "@mui/material";
import modalBg from "assets/img/reward_modal_bg.png";
import fireworks from "assets/img/fireworks.png";
import {ReactComponent as TrophyIcon} from "assets/img/trophy_icon.svg";
import {ReactComponent as BadgeIcon} from "assets/img/badge_icon.svg";
import {last} from "lodash";
import {Avatar} from "components/Leaderboard/leaderboardContent/leaderboards/leaderboardTable/styles";
import defaultAvatar from "assets/img/avatar_MLB.png";

const Wrapper = styled.div`
	font-family: var(--fontFamilyBase);
`;

const NextButton = styled.button`
	background: #2687ff !important;
	color: #fff !important;
	z-index: 99 !important;
`;

const ContentBox = styled(Box)`
	width: 100%;
	min-height: 540px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	flex: 1;
	position: relative;
	padding: 70px 0 10px;
	background: url(${modalBg}) no-repeat 50% 50%;
	background-size: cover;
	gap: 40px;

	&:before {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100px;
		content: "";
		display: inline-block;
		background: linear-gradient(to bottom, #090b0f, transparent);
	}

	&:after {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 100px;
		content: "";
		display: inline-block;
		background: linear-gradient(to top, #090b0f, transparent);
	}
`;
const InfoBox = styled(Box)`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding: 0 24px;

	.avatar {
		& > img {
			width: 50px;
			height: 50px;
			border-radius: 10px;
		}
	}
`;
const Title = styled(Box)`
	color: #fff;
	text-align: center;
	font-size: 24px;
	font-style: normal;
	font-weight: 800;
	line-height: normal;
`;
const Description = styled(Box)`
	color: #fff;
	text-align: center;
	font-size: 14px;
	font-style: normal;
	font-weight: 500;
	line-height: normal;
`;
const ImageContainer = styled(Box)`
	display: flex;
	justify-content: center;
	align-items: center;
	position: relative;
	padding: 20px 0;
`;

const FireBox = styled(Box)`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: 3;
	background: url(${fireworks}) no-repeat 50% 50%;
	background-size: contain;
`;
const ProgressBox = styled(Box)`
	display: flex;
	gap: 30px;
	justify-content: space-between;
	align-items: stretch;
	font-family: var(--fontFamilyBase);
`;
const ProgressCell = styled(Box)`
	flex: 1 1 100%;
	display: flex;
	flex-direction: column;
	gap: 8px;

	.MuiLinearProgress-root {
		background-color: #30353c;

		.MuiLinearProgress-bar {
			background-color: #a442f1;
		}
	}

	p {
		color: #fff;
		font-size: 18px;
		font-style: normal;
		font-weight: 400;
		line-height: 44px;
	}
`;
const PercentCell = styled(Box)`
	flex: 1 1 120px;
	display: flex;
	flex-direction: column;
	//gap: 6px;

	p {
		color: #a442f1;
		text-align: right;
		font-size: 12px;
		font-style: normal;
		font-weight: 700;
		line-height: normal;
	}
`;
const PercentBox = styled(Box)`
	color: #fff;
	text-align: right;
	font-size: 36px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
`;

export const RewardModal: React.FC = () => {
	const i18n = useTranslation();
	const isMobile = useIsMobile();
	const dispatch = useDispatch();
	const isViewed = useSelector(isRewardViewed);
	const loadState = useSelector(loadingUserState);
	const avatar = useSelector(getUserAvatar);
	const userRewards = useSelector(getUserRewards);
	const lastUserReward = last(userRewards);
	const [currentStep, setCurrentStep] = useState(1);
	const isHidden = [isViewed, loadState !== RequestState.Received].some(Boolean);
	const currentRewardStats = useSelector(getRewardStatsByName)(lastUserReward ?? "streak_5");

	useEffect(() => {
		if (!isHidden) {
			dispatch(fetchUserRewards());
		}
	}, [dispatch, isHidden]);

	const onSkip = () => {
		dispatch(setViewRewardModal());
		setCurrentStep(1);
	};

	const onCloseSkip = () => {
		// trackAction("currentStepObject.exitAction");
		onSkip();
	};

	const toNextStep = () => {
		setCurrentStep(currentStep + 1);
	};

	if (isHidden) {
		return null;
	}

	const Content = () => (
		<Wrapper>
			<ContentBox>
				<Exist when={currentStep === 0}>
					<InfoBox>
						<Box sx={{display: "flex", justifyContent: "center"}}>
							<Avatar levelColor={"#62F0CE"} className={"avatar"}>
								<img src={avatar || defaultAvatar} alt={`user avatar`} />
							</Avatar>
						</Box>
						<Title>{i18n.t("modal.reward.title_step_1", "You Earned A Trophy")}</Title>
						<ImageContainer>
							<FireBox />
							<TrophyIcon />
						</ImageContainer>
						<Description>
							{i18n.t(
								"modal.reward.description_step_1",
								"Flexitarian live-edge artisan, ugh dreamcatcher cray hot chicken franzen jean shorts. Banh mi poutine celiac af skateboard. Chillwave pitchfork gastropub, live-edge sustainable letterpress."
							)}
						</Description>
					</InfoBox>
					<ButtonWithIcon
						text={i18n.t("modal.reward.button_next", "Next")}
						styleExtend={NextButton}
						onClick={toNextStep}
					/>
				</Exist>

				<Exist when={currentStep === 1}>
					{lastUserReward && (
						<Fragment>
							<InfoBox>
								{/*<Box sx={{display: "flex", justifyContent: "center"}}>*/}
								{/*	<Avatar levelColor={"#62F0CE"} className={"avatar"}>*/}
								{/*		<img src={avatar || defaultAvatar} alt={`user avatar`} />*/}
								{/*	</Avatar>*/}
								{/*</Box>*/}
								<Title>
									{i18n.t("modal.reward.title_step_2", "You Unlocked A Badge")}
								</Title>
								<ImageContainer>
									<FireBox />
									<BadgeIcon />
								</ImageContainer>
								<Title>
									{i18n.t(`modal.reward.badge_${lastUserReward}`, "Badge Name")}
								</Title>

								{currentRewardStats && (
									<ProgressBox>
										<ProgressCell>
											<p>
												{i18n.t(
													"badges.players_with_badges",
													"Players with badge"
												)}
											</p>
											<LinearProgress
												variant="determinate"
												value={currentRewardStats.earnedPercentage}
											/>
										</ProgressCell>

										<PercentCell>
											<PercentBox>
												{currentRewardStats.earnedPercentage}%
											</PercentBox>
											<p>{i18n.t("badges.rare_copy", "Rare")}</p>
										</PercentCell>
									</ProgressBox>
								)}
							</InfoBox>
							<ButtonWithIcon
								text={i18n.t("modal.reward.button_close", "Ok")}
								styleExtend={NextButton}
								onClick={onCloseSkip}
							/>
						</Fragment>
					)}
				</Exist>
			</ContentBox>
		</Wrapper>
	);

	const props = {
		isOpen: true,
		onClose: onCloseSkip,
		name: "reward",
		headerText: i18n.t("modal.reward_header.title", "Your Results"),
	};

	return (
		<Fragment>
			<Exist when={isMobile}>
				<HOCModal {...props}>
					<Content />
				</HOCModal>
			</Exist>
			<Exist when={!isMobile}>
				<HOCModalCenterDesktopPosition {...props}>
					<Content />
				</HOCModalCenterDesktopPosition>
			</Exist>
		</Fragment>
	);
};
